import { useEffect, useState, useCallback } from 'react';
import { getCalendar, getAIRecommendation, CalendarData, CalendarPlan, Workout } from '../api/client';
import { CalendarView } from '../components/CalendarView';
import { LoadingState, ErrorState } from '../components/States';
import { haptic } from '../lib/telegram';
import { toLocalDateString, isFuture, formatLongDate } from '../lib/date';
import './CalendarPage.css';

interface CalendarPageProps {
  userId: number;
  onNavigate: (page: string, data?: any) => void;
}

export const CalendarPage: React.FC<CalendarPageProps> = ({ userId, onNavigate }) => {
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);

  const loadCalendar = useCallback(async (year: number, month: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCalendar(userId, year, month);
      setCalendarData(data);
      setCurrentYear(year);
      setCurrentMonth(month);
    } catch (err: any) {
      setError(err?.message || 'Ошибка загрузки календаря');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadCalendar(currentYear, currentMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleDateSelect = (date: Date) => {
    haptic.light();
    setSelectedDate(date);
  };

  const handleMonthChange = (year: number, month: number) => {
    if (year !== currentYear || month !== currentMonth) {
      loadCalendar(year, month);
    }
  };

  const selectedDayWorkout: Workout | undefined = selectedDate
    ? calendarData?.workouts.find(w => w.date === toLocalDateString(selectedDate))
    : undefined;

  const selectedDayPlan: CalendarPlan | undefined = selectedDate
    ? calendarData?.plans.find(p => p.date === toLocalDateString(selectedDate))
    : undefined;

  const handleGenerate = async () => {
    if (!selectedDate) return;
    setGenerating(true);
    try {
      await getAIRecommendation(userId, toLocalDateString(selectedDate));
      haptic.success();
      await loadCalendar(currentYear, currentMonth);
    } catch (err: any) {
      haptic.error();
      setError(err?.message || 'Не удалось создать план');
    } finally {
      setGenerating(false);
    }
  };

  const selectedFuture = selectedDate ? isFuture(selectedDate) : false;

  return (
    <div className="calendar-page">
      <header className="calendar-header">
        <span>RUN LAB / SCHEDULE</span>
        <h1>Календарь</h1>
      </header>

      {loading ? (
        <LoadingState text="Загрузка календаря…" />
      ) : error && !calendarData ? (
        <ErrorState message={error} onRetry={() => loadCalendar(currentYear, currentMonth)} />
      ) : (
        <>
          <CalendarView
            calendarData={calendarData}
            onDateSelect={handleDateSelect}
            onMonthChange={handleMonthChange}
            selectedDate={selectedDate}
          />

          {selectedDate && (
            <div className="selected-day-info">
              <h3>{formatLongDate(toLocalDateString(selectedDate))}</h3>

              {selectedDayWorkout ? (
                <div className="day-section workout-section">
                  <h4>Тренировка выполнена</h4>
                  <div className="exercises-summary">
                    {selectedDayWorkout.exercises.map((ex, idx) => (
                      <div key={ex.id ?? idx} className="exercise-row">
                        <span className="ex-name">{ex.name}</span>
                        <span className="ex-meta">
                          {ex.sets ? `${ex.sets}×` : ''}{ex.reps ?? ''}{ex.weight ? ` · ${ex.weight}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    className="view-details-btn"
                    onClick={() => { haptic.light(); onNavigate('workout-detail', { workoutId: selectedDayWorkout.id }); }}
                  >
                    Подробнее →
                  </button>
                </div>
              ) : selectedDayPlan?.ai_recommendation ? (
                <div className="day-section plan-section">
                  <h4>Запланировано</h4>
                  <p className="focus-text">
                    <strong>Фокус:</strong> {selectedDayPlan.ai_recommendation.focus}
                  </p>
                  <div className="exercises-summary">
                    {selectedDayPlan.ai_recommendation.exercises.slice(0, 3).map((ex, idx) => (
                      <div key={idx} className="exercise-row">
                        <span className="ex-name">{ex.name}</span>
                        <span className="ex-meta">{ex.sets}×{ex.reps}</span>
                      </div>
                    ))}
                    {selectedDayPlan.ai_recommendation.exercises.length > 3 && (
                      <div className="more-exercises">
                        +{selectedDayPlan.ai_recommendation.exercises.length - 3} ещё
                      </div>
                    )}
                  </div>
                  <p className="plan-notes">{selectedDayPlan.ai_recommendation.notes}</p>
                </div>
              ) : selectedFuture ? (
                <div className="day-section empty-section">
                  <p>На этот день нет плана</p>
                  <button
                    className="generate-btn"
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    {generating ? 'Создаём план…' : 'Создать план на день'}
                  </button>
                </div>
              ) : (
                <div className="day-section empty-section muted-section">
                  <p>В этот день тренировок не было</p>
                </div>
              )}

              {error && <p className="error-inline">{error}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
};
